import type React from 'react';

/**
 * Создаёт типобезопасный слот расширения.
 *
 * Слот — точка расширения, которую базовый модуль предоставляет
 * клиентским модулям для добавления контента без изменения базового кода.
 *
 * Регистрация происходит в bootstrap.ts клиентского модуля (до рендера приложения).
 * Все зарегистрированные обработчики складываются в массив и вызываются при рендере.
 *
 * @example
 * // Определение слота в базовом модуле:
 * export const mySlot = createSlot<{ entity: Entity; reload: () => void }>();
 *
 * // Регистрация в bootstrap.ts клиентского модуля:
 * mySlot.register(({ entity, reload }) => <MyButton entity={entity} onSuccess={reload} />);
 *
 * // Использование в компоненте:
 * const extensions = mySlot.getAll();
 * {extensions.map((fn, i) => <Fragment key={i}>{fn(payload)}</Fragment>)}
 */
export function createSlot<TPayload, TReturn = React.ReactNode>() {
  const handlers: ((payload: TPayload) => TReturn)[] = [];

  return {
    /**
     * Регистрирует обработчик расширения.
     * Вызывается один раз в bootstrap.ts клиентского модуля.
     */
    register(handler: (payload: TPayload) => TReturn): void {
      handlers.push(handler);
    },

    /**
     * Возвращает все зарегистрированные обработчики.
     * Вызывается внутри React-компонента при рендере.
     */
    getAll(): ((payload: TPayload) => TReturn)[] {
      return [...handlers];
    },
  };
}
